import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { API_BASE_URI } from "../../http/api";
import { WeightedChar } from "../../types/EvaluationLibrary/weighted-char";
import { DetailRequestBuilder, DetailRequest } from "../../http/detail";
import { Filter, FilterBuilder } from "../../http/filter";
import { Collection } from "../../http/collection";
import { SearchRequest, SearchResponse } from '../../http/search';
import { Observable, throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Dot } from "@core/utils";

export class WeightedCharCreated {
    static readonly type = "[WeightedCharService] WeightedChar Created";
    constructor(public weightedChar: WeightedChar) {}
}

export class WeightedCharUpdated {
    static readonly type = "[WeightedCharService] WeightedChar Updated";
    constructor(public weightedChar: WeightedChar) {}
}

export class WeightedCharDeleted {
    static readonly type = "[WeightedCharService] WeightedChar Deleted";
    constructor(public weightedChar: WeightedChar) {}
}

export class DuplicateLabelError extends HttpErrorResponse {}

@Injectable()
export class WeightedCharService {

    constructor(
        private _http: HttpClient,
        private _store: Store,
        @Inject(API_BASE_URI) private _baseUri: string
    ) {}

    get(key: string, detail?: DetailRequest): Observable<WeightedChar> {
        const params = detail ? DetailRequestBuilder.queryParams(detail) : {};
        return this._http.get<WeightedChar>(`${this._baseUri}/evaluation-library/weighted-char/${key}`, {params});
    }

    create(model: Partial<WeightedChar>) {
        return this._http.post<WeightedChar>(`${this._baseUri}/evaluation-library/weighted-char`, model)
            .pipe(
                this.catchDupliacteLabelError(),
                tap(char => this._store.dispatch(new WeightedCharCreated(char)))
            );
    }

    update(key: string, model: Partial<WeightedChar>) {
        return this._http.put<WeightedChar>(`${this._baseUri}/evaluation-library/weighted-char/${key}`, model)
            .pipe(
                this.catchDupliacteLabelError(),
                tap(char => this._store.dispatch(new WeightedCharUpdated(char)))
            );
    }

    delete(key: string) {
        return this._http.delete<WeightedChar>(`${this._baseUri}/evaluation-library/weighted-char/${key}`)
            .pipe(tap(char => this._store.dispatch(new WeightedCharDeleted(char))));
    }

    query(filter: Filter, detail?: DetailRequest): Observable<Collection<WeightedChar>> {
        const params = {
            ...FilterBuilder.queryParams(filter),
            ...DetailRequestBuilder.queryParams(detail),
        };

        return this._http.get<Collection<WeightedChar>>(`${this._baseUri}/evaluation-library/weighted-char`, {params});
    }

    search(search: SearchRequest) {
        return this._http.get<SearchResponse<WeightedChar>>(`${this._baseUri}/evaluation-library/weighted-char/search`, {params: <any>search});
    }

    private catchDupliacteLabelError() {
        return catchError<WeightedChar, Observable<WeightedChar>>(e => {
            if (e instanceof HttpErrorResponse && e.status === 422
                && String(Dot.get(e.error, 'errors.label', '')).includes('taken') ) {
                    return throwError(new DuplicateLabelError(e));
            }

            return throwError(e);
        });
    }
}